import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import StreakBadge from '../components/StreakBadge';
import CalendarHeatmapBlur from '../components/CalendarHeatmapBlur';
import { CompletionRateChart, DayOfWeekChart } from '../components/StatsChart';
import HabitForm from '../components/HabitForm';
import FeatureGate from '../components/FeatureGate';
import StreakFreezeModal from '../components/StreakFreezeModal';
import useStore from '../store';
import { useActiveUser } from '../hooks/useActiveUser';
import { summarizeHabit } from '../utils/streakCalc';
import { checkinsRepo } from '../services/db/checkins';
import { freezesRepo } from '../services/db/freezes';

export default function HabitDetail() {
  const { habitId } = useParams();
  const navigate = useNavigate();
  const { isAuthed, userId } = useActiveUser();
  const { habits, allCheckins, updateHabit, deleteHabit, fetchHabits } = useStore();
  const isPremium = useStore((s) => s.isPremium);
  const habit = habits.find((h) => h.habitId === habitId);

  const [history, setHistory] = useState([]);
  const [freezes, setFreezes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (habits.length === 0) fetchHabits();
  }, [habits.length, fetchHabits]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      // Always resolve the loading state — an early return that left `loading`
      // true would show the skeleton forever when userId is momentarily absent.
      if (habitId && userId) {
        try {
          const [dates, frozen] = await Promise.all([
            checkinsRepo.listForHabit(userId, habitId),
            freezesRepo.listForHabit(userId, habitId),
          ]);
          if (mounted) { setHistory(dates); setFreezes(frozen); }
        } catch (err) {
          console.error('Failed to load history:', err);
        }
      }
      if (mounted) setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, [habitId, userId, allCheckins]);

  const stats = useMemo(() => {
    if (!habit) return null;
    return summarizeHabit(habit, history, new Date(), freezes);
  }, [habit, history, freezes]);

  if (!habit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">Habit not found</p>
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 dark:text-primary-400 font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  async function handleEdit(updates) {
    try {
      await updateHabit(habitId, updates);
      toast.success('Habit updated');
    } catch {
      toast.error('Failed to update');
      throw new Error('update failed');
    }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await deleteHabit(habitId);
      toast.success('Habit archived');
      navigate('/');
    } catch {
      toast.error('Failed to archive');
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  }

  const color = habit.color || '#6366F1';
  const currentStreak = stats?.currentStreak ?? 0;
  const bestStreak = stats?.bestStreak ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-8">
      <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center gap-3 shadow-sm safe-area-top">
        <button
          onClick={() => navigate('/')}
          aria-label="Back to dashboard"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-xl">{habit.icon}</span>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
            {habit.name}
          </h1>
        </div>
        <button
          onClick={() => setShowEdit(true)}
          aria-label="Edit habit"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-4">
        <StreakBadge
          streak={currentStreak}
          bestStreak={bestStreak}
          color={color}
        />

        {currentStreak > 0 && isAuthed && (
          <button
            onClick={() => {
              if (isPremium()) {
                setShowFreezeModal(true);
              } else {
                navigate('/premium?source=streak_freeze');
              }
            }}
            className="w-full py-2.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors flex items-center justify-center gap-2"
          >
            <span>🧊</span>
            <span>Streak Freeze</span>
            {!isPremium() && (
              <span className="text-xs px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full">
                Premium
              </span>
            )}
          </button>
        )}

        <CalendarHeatmapBlur checkinDates={history} color={color} />

        {loading && (
          <div className="space-y-4">
            <div className="h-24 rounded-2xl skeleton" />
            <div className="h-48 rounded-2xl skeleton" />
          </div>
        )}

        {!loading && stats && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">30-Day Rate</p>
                  <p className="text-2xl font-bold" style={{ color }}>
                    {stats.completionRate30d}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Check-ins</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats.totalCompletions}
                  </p>
                </div>
              </div>
            </div>

            <FeatureGate feature="advancedAnalytics">
              <CompletionRateChart weeklyRates={stats.weeklyRates} color={color} />
            </FeatureGate>
            <DayOfWeekChart dayOfWeekCounts={stats.dayOfWeekCounts} color={color} />
          </>
        )}

        <button
          onClick={() => setShowArchiveConfirm(true)}
          className="w-full py-3 text-red-500 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-colors"
        >
          Archive Habit
        </button>
      </div>

      {showEdit && (
        <HabitForm
          habit={habit}
          onSave={handleEdit}
          onClose={() => setShowEdit(false)}
        />
      )}

      {showFreezeModal && (
        <StreakFreezeModal
          habit={habit}
          currentStreak={currentStreak}
          onClose={() => setShowFreezeModal(false)}
          onFreezeComplete={async () => {
            const frozen = await freezesRepo.listForHabit(userId, habitId).catch(() => freezes);
            setFreezes(frozen);
            fetchHabits();
          }}
        />
      )}

      {showArchiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !archiving && setShowArchiveConfirm(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl p-6 animate-scale-in" role="dialog" aria-modal="true" aria-labelledby="archive-title">
            <h2 id="archive-title" className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Archive this habit?
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              <strong>{habit.name}</strong> and its streak history will be removed from your
              dashboard. This can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowArchiveConfirm(false)}
                disabled={archiving}
                className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                disabled={archiving}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
              >
                {archiving ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
